import React from 'react';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import {
  Box,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@availity/element';

// component for individual table rows
export const OAAAdminChangeTableRow = ({ ...props }) => {
  const { rowNumber, organization = {}, reasonOptions } = props;
  const { register, formState = {}, watch, setValue, clearErrors } = useFormContext();
  const { errors } = formState;
  const { orgName, customerId, appId, lineAddress, address, createDate, taxId, npi } = organization;

  const reasonValue = watch(`${appId}.reason`);

  return (
    <TableRow>
      <TableCell id={`${appId}-row-number-${rowNumber}`} sx={{ alignContent: 'start' }}>
        <Typography variant="subtitle1">{rowNumber}</Typography>
      </TableCell>
      <TableCell
        id={`${appId}-organization-info`}
        sx={{
          alignContent: 'start',
          width: {
            xs: '350px',
            sm: '500px',
          },
        }}
      >
        <Grid display="flex" flexDirection="row" flexWrap="wrap">
          <Box sx={{ marginRight: 4, marginBottom: 1 }}>
            <Grid display="flex" flexDirection="column">
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Organization Name
                </Box>
              </Typography>
              <Typography variant="subtitle1">{orgName}</Typography>
            </Grid>
          </Box>
          <Box sx={{ marginRight: 4, marginBottom: 1 }}>
            <Grid display="flex" flexDirection="column">
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Customer ID
                </Box>
              </Typography>
              <Typography variant="subtitle1">{customerId}</Typography>
            </Grid>
          </Box>
        </Grid>
        <Grid display="flex" flexDirection="row" flexWrap="wrap">
          <Box sx={{ marginRight: 4, marginBottom: 1 }}>
            <Grid display="flex" flexDirection="column">
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Address
                </Box>
              </Typography>
              <Box>
                <Typography variant="subtitle1">{lineAddress}</Typography>
                <Typography variant="subtitle1">{address}</Typography>
              </Box>
            </Grid>
          </Box>
        </Grid>
        <Grid display="flex" flexDirection="row" flexWrap="wrap">
          <Box sx={{ marginRight: 4, marginBottom: 1 }}>
            <Grid display="flex" flexDirection="column">
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Create Date
                </Box>
              </Typography>
              <Typography variant="subtitle1">{createDate}</Typography>
            </Grid>
          </Box>
          <Box sx={{ marginRight: 4, marginBottom: 1 }}>
            <Grid display="flex" flexDirection="column">
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Tax ID
                </Box>
              </Typography>
              <Typography variant="subtitle1">{taxId || 'N/A'}</Typography>
            </Grid>
          </Box>
          <Box sx={{ marginRight: 4, marginBottom: 1 }}>
            <Grid display="flex" flexDirection="column">
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  NPI
                </Box>
              </Typography>
              <Typography variant="subtitle1">{npi || 'N/A'}</Typography>
            </Grid>
          </Box>
        </Grid>
      </TableCell>
      <TableCell id={`${appId}-actions`} sx={{ minWidth: '350px' }}>
        <TextField
          id={`${appId}.reason`}
          label="Reason for change"
          margin="normal"
          {...register(`${appId}.reason`, {
            required: 'Select a reason.',
          })}
          select
          value={reasonValue}
          error={errors[appId]?.reason}
          helperText={errors[appId]?.reason?.message}
          onChange={(e) => {
            const { value } = e.target;
            setValue(`${appId}.reason`, value);

            if (value) {
              clearErrors(`${appId}.reason`);
            }
          }}
          required
        >
          {reasonOptions.map((option) => (
            <MenuItem key={`option-${option.value}`} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        {['0', '1', '4'].indexOf(reasonValue) >= 0 && (
          <TextField
            id={`${appId}.admin`}
            label="If you know, who should be the new primary administrator?"
            margin="normal"
            {...register(`${appId}.admin`, {
              maxLength: {
                value: 250,
                message: 'You have exceeded the 250 character limit.',
              },
              pattern: {
                value: /^[\d A-Za-z]*$/,
                message: 'Enter a value using only alphanumeric characters.',
              },
            })}
            error={errors[appId]?.admin}
            helperText={errors[appId]?.admin?.message}
          />
        )}
        <TextField
          id={`${appId}.comments`}
          label="Comments"
          margin="normal"
          {...register(`${appId}.comments`, {
            maxLength: {
              value: 250,
              message: 'You have exceeded the 250 character limit.',
            },
            pattern: {
              value: /^[\d A-Za-z]*$/,
              message: 'Enter a value using only alphanumeric characters.',
            },
          })}
          error={errors[appId]?.comments}
          helperText={errors[appId]?.comments?.message}
        />
      </TableCell>
    </TableRow>
  );
};

OAAAdminChangeTableRow.propTypes = {
  rowNumber: PropTypes.number,
  organization: PropTypes.object.isRequired,
  reasonOptions: PropTypes.array.isRequired,
};

// main table component
const OAAAdminChangeTable = ({ ...props }) => {
  const { organizations = [], reasonOptions } = props;
  const { formState = {} } = useFormContext();
  const { errors } = formState;

  return (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight: '500px',
        borderColor: isEmpty(errors) ? '#292B32' : '#C70002',
      }}
    >
      <Table stickyHeader aria-label="OAA Admin Change Table">
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Organization Info
                </Box>
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Actions
                </Box>
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {organizations.map((organization, index) => {
            const { appId } = organization;
            const rowNumber = index + 1;

            return (
              <OAAAdminChangeTableRow
                key={`${appId}-oaa-admin-change-table-row`}
                rowNumber={rowNumber}
                organization={organization}
                reasonOptions={reasonOptions}
              />
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

OAAAdminChangeTable.propTypes = {
  organizations: PropTypes.array,
  reasonOptions: PropTypes.array.isRequired,
};

export default OAAAdminChangeTable;
